import React, { useEffect, useState } from 'react';
import { useQuery } from '@common/hooks';
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Typography,
  Select,
} from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  ErrorHandler,
  InfoTooltip,
  LoadingState,
  CurrencyInput,
} from '@common/components';
import { ShiftTypesSelectInput } from '@jobs/components';
import {
  ASSIGNMENT_TERM_QUERY,
  WORKS_USERS_QUERY,
} from './edit-assignment-term-helper';
import { WorksUsersSelect } from 'works/legacy/components/form-fields/works-users-select';
import OrganizationUnitSelect from 'works/legacy/components/organization-unit-select/organization-unit-select';
import { OvertimeThresholdSelect } from '@timekeeping/components';

const { Title } = Typography;

export function EditAssignmentTermForm({
  assignmentTermId,
  form,
  onSubmit,
  isExtension,
  visible,
}) {
  const { error, data, loading } = useQuery(ASSIGNMENT_TERM_QUERY, {
    variables: { id: assignmentTermId },
  });
  const [departmentId, setDepartmentId] = useState();

  const {
    error: worksUsersError,
    data: worksUsersData,
    loading: worksUsersLoading,
  } = useQuery(WORKS_USERS_QUERY, {
    variables: { departmentId: departmentId },
    skip: !departmentId,
  });

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setDepartmentId(data?.assignmentTerm?.organizationUnit?.department?.id);
    }
  }, [visible, form, data]);

  useEffect(() => {
    setDepartmentId(data?.assignmentTerm?.organizationUnit?.department?.id);
  }, [data]);

  if (loading && !data) {
    return <LoadingState />;
  }

  if (error || worksUsersError) {
    return <ErrorHandler error={error} log="EditAssignmentTermModal" />;
  }

  const assignmentTermData = data.assignmentTerm;
  const effectiveAt = moment(assignmentTermData.effectiveAt);
  const startDate = moment(assignmentTermData.startDate);
  const endDate = moment(assignmentTermData.endDate);

  const initialValues = {
    ...assignmentTermData,
    effectiveAt: moment.max(startDate, effectiveAt),
    organizationUnitId: assignmentTermData.organizationUnit.id,
    hiringLeaderId: assignmentTermData.hiringLeader.id,
    employmentType: assignmentTermData.employmentType,
  };

  const disabledEffectiveAt = (currentDate) => {
    const startRange = startDate;
    const endRange = endDate;

    return !(
      startRange.isSameOrBefore(currentDate) && endRange.isAfter(currentDate)
    );
  };

  const formItemLayout = {
    className: 'form-item-layout',
    labelAlign: 'left',
    labelCol: { span: 8 },
    wrapperCol: { span: 14 },
  };

  const tooltipMessage = () => {
    if (isExtension) {
      return 'You only need to edit Term Effective date if it is a mid-extension change.';
    }

    return 'You only need to edit Term Effective date if it is a mid-assignment change.';
  };

  return (
    <>
      <Form
        form={form}
        initialValues={initialValues}
        className="EditTermModalForm"
        onFinish={(values) => onSubmit(values)}
      >
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Form.Item
              className="form-item-effective-at-layout"
              labelAlign="left"
              labelCol={{ span: 8 }}
              colon={false}
              label={
                <Title level={4} className="term-effective-label">
                  Term Effective:
                </Title>
              }
            >
              <Row align="middle">
                <Col span={20}>
                  <Form.Item
                    name="effectiveAt"
                    noStyle
                    required
                    rules={[
                      {
                        required: true,
                        message: 'Please select an effective date',
                      },
                    ]}
                  >
                    <DatePicker
                      picker="date"
                      placeholder="mm/dd/yyyy"
                      format="MM/DD/YYYY"
                      disabledDate={disabledEffectiveAt}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <InfoTooltip title={tooltipMessage()} />
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Form.Item
              name="billingRegularRate"
              label="Rate"
              rules={[
                {
                  required: true,
                  message: 'Please enter the billing rate',
                },
              ]}
              {...formItemLayout}
            >
              <CurrencyInput />
            </Form.Item>

            <ShiftTypesSelectInput {...formItemLayout} />
            <Form.Item
              name="shiftsPerWeek"
              label="Shifts Per Week"
              rules={[
                {
                  required: true,
                  message: 'Please enter the shifts per week value',
                },
              ]}
              {...formItemLayout}
            >
              <InputNumber />
            </Form.Item>
            <Form.Item
              name="hoursPerShift"
              label="Hours Per Shift"
              rules={[
                {
                  required: true,
                  message: 'Please enter the hours per shift value',
                },
              ]}
              {...formItemLayout}
            >
              <InputNumber />
            </Form.Item>
            {!!assignmentTermData.overtimeThreshold && (
              <OvertimeThresholdSelect style={formItemLayout} />
            )}
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              className="reset-margin-bottom"
              name="timeoffDescription"
              label="Time Off"
              rules={[
                {
                  required: true,
                  message: 'Please enter the time off',
                },
              ]}
              {...formItemLayout}
            >
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 5 }}
                maxLength={150}
              />
            </Form.Item>
          </Col>
        </Row>

        <OrganizationUnitSelect
          onChange={(organizationUnit) => {
            setDepartmentId(organizationUnit.department.id);
            form.setFieldsValue({ hiringLeaderId: '' });
          }}
          {...formItemLayout}
          size="default"
        />
        <Form.Item
          label="Hiring Leader"
          name="hiringLeaderId"
          rules={[
            {
              required: true,
              message: 'Please select the hiring leader',
            },
          ]}
          {...formItemLayout}
        >
          <WorksUsersSelect
            loading={loading || worksUsersLoading}
            worksUsers={worksUsersData?.worksUsers}
          />
        </Form.Item>

        <Form.Item
          name="employmentType"
          label="Employment Type"
          rules={[
            {
              required: true,
              message: 'Please select the employment type',
            },
          ]}
          {...formItemLayout}
        >
          <Select>
            <Select.Option value="traveler">Traveler</Select.Option>
            <Select.Option value="local">Local</Select.Option>
            <Select.Option value="direct_sourcing">
              Direct Sourcing
            </Select.Option>
          </Select>
        </Form.Item>
      </Form>
      <style jsx>{`
        :global(.EditTermModalForm .ant-input-number),
        :global(.EditTermModalForm .ant-form-item-control-input-content),
        :global(.EditTermModalForm .ant-picker) {
          width: 100%;
        }
        :global(.EditTermModalForm .form-item-layout) {
          margin-bottom: 8px;
        }
        :global(.EditTermModalForm .form-item-effective-at-layout) {
          margin-bottom: 8px;
          align-items: 'flex-end';
        }
        :global(.EditTermModalForm .reset-margin-bottom) {
          margin-bottom: 0;
        }
        :global(.EditTermModalForm .reset-padding-top-right) {
          padding-top: 0;
          padding-right: 0;
        }
      `}</style>
    </>
  );
}

EditAssignmentTermForm.propTypes = {
  assignmentTermId: PropTypes.string.isRequired,
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
    resetFields: PropTypes.func.isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isExtension: PropTypes.bool.isRequired,
  visible: PropTypes.bool.isRequired,
};
